
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { createAssessment } from '@/services/assessment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

export default function CreateAssessment() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isMaturityModel, setIsMaturityModel] = useState(false);

  const formatSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    // Automatically generate slug from title if slug is empty
    if (!slug) {
      setSlug(formatSlug(newTitle));
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(formatSlug(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required"
      });
      return;
    }

    if (!slug.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Slug is required"
      });
      return;
    }

    setLoading(true);
    try {
      // Use the createAssessment function from our service instead of direct database call
      const newAssessment = await createAssessment({
        title,
        description,
        slug,
        isMaturityModel
      });
      
      toast({
        title: "Success",
        description: "Assessment created successfully"
      });
      
      navigate(`/admin/assessments/${newAssessment.id}`);
    } catch (error) {
      console.error("Error creating assessment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create assessment"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            placeholder="Enter assessment title"
            value={title}
            onChange={handleTitleChange}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            placeholder="enter-assessment-slug"
            value={slug}
            onChange={handleSlugChange}
            required
            className="font-mono"
          />
          <p className="text-sm text-muted-foreground">
            This will be used in the URL: example.com/assessment/{slug || 'your-slug'}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Enter assessment description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </div>
        
        <div className="flex items-center justify-between space-x-2 border p-4 rounded-md">
          <div className="space-y-1">
            <Label htmlFor="is-maturity-model" className="flex flex-col space-y-1">
              <span className="flex items-center gap-2">
                Is Maturity Model?
                {isMaturityModel && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                    Maturity Model
                  </Badge>
                )}
              </span>
              <span className="font-normal text-sm text-muted-foreground">
                Maturity models use fixed level-based questions and cannot be changed after creation
              </span>
            </Label>
          </div>
          <Switch
            id="is-maturity-model"
            checked={isMaturityModel}
            onCheckedChange={setIsMaturityModel}
          />
        </div>
      </div>

      <div className="flex gap-4">
        <Button type="submit" disabled={loading}>
          Create Assessment
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => navigate('/admin/assessments')}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
